import React, { useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { LinkWithUTM as Link } from "./LinkWithUTM";
import logoPrimary from "../images/logos/logo_primary.svg";

/* global _paq */

const conditionLinks = [
	["Dementia care", "/condition/dementia/"],
	["Parkinson's care", "/condition/parkinsons/"],
	["Stroke care", "/condition/stroke/"],
	["Cancer care", "/condition/cancer/"],
	["Multiple sclerosis care", "/condition/multiple-sclerosis/"],
];

const careLinks = [
	["Live-in care", "/liveincare/"],
	["Companionship and day-to-day support", "/companionship-care/"],
	["Couples care", "/couples-care/"],
	["Professional carers", "/our-carers/"],
	["Pricing", "/pricing/"],
];

const locationLinks = [
	["London", "/location/london/"],
	["South East", "/location/south-east/"],
	["South West", "/location/south-west/"],
	["East Midlands", "/location/east-midlands/"],
	["West Midlands", "/location/west-midlands/"],
	["West of England", "/location/west-of-england/"],
];

const menuFeatures = {
	care: {
		theme: "care",
		eyebrow: "Fully managed care",
		title: "Care built around real life.",
		description:
			"See how a professional carer, local care team and thoughtful technology work together.",
		label: "Explore our approach",
		href: "/approach/",
		visual: "care-photo",
	},
	conditions: {
		theme: "conditions",
		eyebrow: "Free live-in care guide",
		title: "Make the options feel clearer.",
		description:
			"Understand care, likely costs and the practical next steps for your family.",
		label: "Send me the guide",
		href: "/request-a-brochure/",
		visual: "guide",
	},
	locations: {
		theme: "locations",
		eyebrow: "Care across England",
		title: "Start with a local conversation.",
		description:
			"Talk through what is changing at home with a Care Advisor who knows your region.",
		label: "Talk to a Care Advisor",
		href: "/care-consultation/",
		visual: "advisors",
	},
};

const trackNavigation = (label) => {
	if (typeof _paq !== "undefined") {
		_paq.push(["trackEvent", "Navigation", "Click", label]);
	}
};

const advisorsAreAvailable = () => {
	const londonTime = new Intl.DateTimeFormat("en-GB", {
		timeZone: "Europe/London",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).formatToParts(new Date());
	const timePart = (type) =>
		Number(londonTime.find((part) => part.type === type)?.value || 0);
	const minutesSinceMidnight = timePart("hour") * 60 + timePart("minute");

	return minutesSinceMidnight >= 9 * 60 && minutesSinceMidnight <= 17 * 60 + 30;
};

const MenuFeatureVisual = ({ type }) => {
	if (type === "care-photo") {
		return (
			<div className="site-header__feature-photo" aria-hidden="true">
				<img
					src="/assets/marketing-site/images/live-in-image-01.jpg"
					alt=""
				/>
				<span>Care that feels personal</span>
			</div>
		);
	}

	if (type === "guide") {
		return (
			<div className="site-header__feature-guide" aria-hidden="true">
				<span className="site-header__feature-guide-halo" />
				<img
					src="/assets/marketing-site/images/banner-brochure.png"
					alt=""
				/>
				<span className="site-header__feature-guide-badge">Free guide</span>
			</div>
		);
	}

	if (type === "advisors") {
		return (
			<div className="site-header__feature-advisors" aria-hidden="true">
				<img
					className="site-header__feature-advisor-photo"
					src="/assets/marketing-site/images/care-advisor-office.png"
					alt=""
				/>
			</div>
		);
	}

	return null;
};

const MenuGroup = ({ id, label, links, feature, isOpen, onOpen, onClose }) => (
	<details
		className="site-header__group"
		open={isOpen}
		onMouseEnter={() => {
			onOpen(id);
		}}
		onMouseLeave={() => {
			onClose(id);
		}}
		onBlur={(event) => {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				onClose(id);
			}
		}}
	>
		<summary
			onClick={(event) => {
				event.preventDefault();
				if (isOpen) onClose(id);
				else onOpen(id);
			}}
			aria-expanded={isOpen}
		>
			{label}
		</summary>
		<div className={`site-header__panel site-header__panel--${feature.theme}`}>
			<div className="site-header__panel-links">
				<span className="site-header__panel-label">Explore {label}</span>
				{links.map(([title, href]) => (
					<Link
						className="site-header__panel-link"
						key={href}
						to={href}
						onClick={() => trackNavigation(title)}
					>
						<span>{title}</span>
						<span aria-hidden="true">&#8599;</span>
					</Link>
				))}
			</div>
			<Link
				className="site-header__panel-feature"
				to={feature.href}
				onClick={() => trackNavigation(feature.label)}
			>
				<MenuFeatureVisual type={feature.visual} />
				<span className="site-header__feature-eyebrow">{feature.eyebrow}</span>
				<strong>{feature.title}</strong>
				<span className="site-header__feature-copy">{feature.description}</span>
				<span className="site-header__feature-link">
					{feature.label} <span aria-hidden="true">&#8594;</span>
				</span>
			</Link>
		</div>
	</details>
);

const MobileMenuColumn = ({ label, links, feature, onNavigate }) => (
	<div>
		<p>{label}</p>
		{links.map(([title, href]) => (
			<Link key={href} to={href} onClick={onNavigate}>
				{title}
			</Link>
		))}
		<Link
			className="site-header__mobile-feature"
			to={feature.href}
			onClick={onNavigate}
		>
			<span>{feature.eyebrow}</span>
			<strong>
				{feature.label} <span aria-hidden="true">&#8594;</span>
			</strong>
		</Link>
	</div>
);

export default function Header() {
	const {
		site: {
			siteMetadata: {
				appConfig: { domain: appDomain },
			},
		},
	} = useStaticQuery(graphql`
		query RedesignedHeaderQuery {
			site {
				siteMetadata {
					appConfig {
						domain
					}
				}
			}
		}
	`);
	const [menuOpen, setMenuOpen] = useState(false);
	const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
	const [advisorsAvailable, setAdvisorsAvailable] = useState(false);

	useEffect(() => {
		const updateAvailability = () =>
			setAdvisorsAvailable(advisorsAreAvailable());

		updateAvailability();
		const availabilityTimer = window.setInterval(updateAvailability, 60000);

		return () => window.clearInterval(availabilityTimer);
	}, []);

	useEffect(() => {
		const closeOnEscape = (event) => {
			if (event.key === "Escape") {
				setMenuOpen(false);
				setOpenDesktopMenu(null);
			}
		};
		const closeOnOutsideClick = (event) => {
			if (!event.target.closest(".site-header__group")) {
				setOpenDesktopMenu(null);
			}
		};

		document.addEventListener("keydown", closeOnEscape);
		document.addEventListener("click", closeOnOutsideClick);
		document.body.classList.toggle("menu-open", menuOpen);
		return () => {
			document.removeEventListener("keydown", closeOnEscape);
			document.removeEventListener("click", closeOnOutsideClick);
			document.body.classList.remove("menu-open");
		};
	}, [menuOpen]);

	const closeMenu = () => setMenuOpen(false);
	const closeDesktopMenu = (id) => {
		setOpenDesktopMenu((currentMenu) =>
			currentMenu === id ? null : currentMenu
		);
	};

	return (
		<>
			<a className="skip-link" href="#content-start">
				Skip to main content
			</a>
			<header className="site-header">
				<div className="site-shell site-header__inner">
					<Link
						className="site-header__logo"
						to="/"
						aria-label="Edyn Care home"
					>
						<img src={logoPrimary} alt="Edyn Care" />
					</Link>

					<nav
						className="site-header__desktop-nav"
						aria-label="Primary navigation"
					>
						<MenuGroup
							id="care"
							label="Care"
							links={careLinks}
							feature={menuFeatures.care}
							isOpen={openDesktopMenu === "care"}
							onOpen={setOpenDesktopMenu}
							onClose={closeDesktopMenu}
						/>
						<Link to="/approach/">How Edyn works</Link>
						<MenuGroup
							id="conditions"
							label="Conditions"
							links={conditionLinks}
							feature={menuFeatures.conditions}
							isOpen={openDesktopMenu === "conditions"}
							onOpen={setOpenDesktopMenu}
							onClose={closeDesktopMenu}
						/>
						<MenuGroup
							id="locations"
							label="Locations"
							links={locationLinks}
							feature={menuFeatures.locations}
							isOpen={openDesktopMenu === "locations"}
							onOpen={setOpenDesktopMenu}
							onClose={closeDesktopMenu}
						/>
						<Link to="/blog/">Advice</Link>
						<Link to="/our-team/">About</Link>
					</nav>

					<div className="site-header__actions">
						<div className="site-header__contact">
							<div
								className="site-header__contact-people"
								aria-label="Matthew and Desre, Edyn Care Advisors"
							>
								<img
									src="/assets/marketing-site/images/team/matthew.png"
									alt=""
								/>
								<img
									src="/assets/marketing-site/images/team/desre.png"
									alt=""
								/>
								{advisorsAvailable && (
									<span className="site-header__availability-dot">
										<span className="visually-hidden">
											Care Advisors are available now
										</span>
									</span>
								)}
							</div>
							<a
								className="site-header__phone"
								href="tel:02039709900"
								onClick={() => trackNavigation("Telephone")}
							>
								<span>
									{advisorsAvailable ? "Advisors available" : "Call us"}
								</span>
								<strong>020 3970 9900</strong>
							</a>
						</div>
						<a
							className="button button--primary site-header__cta"
							href={`${appDomain}/login-redirect`}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() => trackNavigation("Client login")}
						>
							Login
						</a>
						<button
							className="site-header__menu-button"
							type="button"
							aria-expanded={menuOpen}
							aria-controls="mobile-navigation"
							onClick={() => setMenuOpen(!menuOpen)}
						>
							<span className="site-header__menu-icon" aria-hidden="true" />
							<span>{menuOpen ? "Close" : "Menu"}</span>
						</button>
					</div>
				</div>

				<nav
					id="mobile-navigation"
					className={`site-header__mobile-nav ${menuOpen ? "is-open" : ""}`}
					aria-label="Mobile navigation"
					aria-hidden={!menuOpen}
				>
					<div className="site-header__mobile-primary">
						<Link to="/approach/" onClick={closeMenu}>
							How Edyn works
						</Link>
						<Link to="/blog/" onClick={closeMenu}>
							Advice
						</Link>
						<Link to="/our-team/" onClick={closeMenu}>
							About Edyn
						</Link>
					</div>
					<div className="site-header__mobile-columns">
						<MobileMenuColumn
							label="Care"
							links={careLinks}
							feature={menuFeatures.care}
							onNavigate={closeMenu}
						/>
						<MobileMenuColumn
							label="Conditions"
							links={conditionLinks}
							feature={menuFeatures.conditions}
							onNavigate={closeMenu}
						/>
						<MobileMenuColumn
							label="Locations"
							links={locationLinks}
							feature={menuFeatures.locations}
							onNavigate={closeMenu}
						/>
					</div>
					<div className="site-header__mobile-actions">
						<a
							className="button button--primary"
							href={`${appDomain}/login-redirect`}
							target="_blank"
							rel="noopener noreferrer"
							onClick={closeMenu}
						>
							Login
						</a>
						<a className="button button--outline" href="tel:02039709900">
							Call 020 3970 9900
						</a>
					</div>
				</nav>
			</header>
			<span id="content-start" className="content-start" tabIndex="-1" />
		</>
	);
}
